import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Package,
  Layers,
  FileText,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  Loader2,
  GripVertical,
  Palette,
  Gauge,
  Droplets,
  Thermometer,
  Zap,
  Activity,
  Cpu,
  Radio,
  Wifi,
  Cog,
  FlaskConical,
  Factory,
  HardDrive
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = [
  { value: "gauge", label: "Gauge/Meter" },
  { value: "droplets", label: "Water/Liquid" },
  { value: "thermometer", label: "Temperature" },
  { value: "zap", label: "Electrical" },
  { value: "activity", label: "Sensor/Analyzer" },
  { value: "cpu", label: "Controller/PLC" },
  { value: "radio", label: "Communication" },
  { value: "wifi", label: "IoT/Wireless" },
  { value: "cog", label: "Automation" },
  { value: "flask-conical", label: "Chemical/Lab" },
  { value: "factory", label: "Industrial" },
  { value: "hard-drive", label: "Data Logger" },
];

// Icon component map
const getIconComponent = (iconName) => {
  const iconMap = {
    "gauge": Gauge,
    "droplets": Droplets,
    "thermometer": Thermometer,
    "zap": Zap,
    "activity": Activity,
    "cpu": Cpu,
    "radio": Radio,
    "wifi": Wifi,
    "cog": Cog,
    "flask-conical": FlaskConical,
    "factory": Factory,
    "hard-drive": HardDrive,
    "package": Package,
    "code": Package,
    "server": Factory,
    "cloud": Wifi,
    "database": HardDrive,
    "shield": Package,
    "network": Radio,
    "settings": Cog,
    "tool": Cog,
  };
  return iconMap[iconName] || Package;
};

const COLOR_OPTIONS = [
  { value: "#007AFF", label: "Blue" },
  { value: "#10B981", label: "Green" },
  { value: "#8B5CF6", label: "Purple" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#EF4444", label: "Red" },
  { value: "#EC4899", label: "Pink" },
  { value: "#06B6D4", label: "Cyan" },
  { value: "#64748B", label: "Slate" },
];

const ProductHierarchyManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", description: "", icon: "package", color: "#007AFF" });
  
  const [showSectionForm, setShowSectionForm] = useState(null); // product_id
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ name: "", description: "" });
  
  const [showSubsectionForm, setShowSubsectionForm] = useState(null); // {product_id, section_id}
  const [editingSubsection, setEditingSubsection] = useState(null);
  const [subsectionForm, setSubsectionForm] = useState({ name: "", description: "" });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
      // Auto-expand all products for better UX
      const expanded = {};
      res.data.forEach(p => { expanded[p.id] = true; });
      setExpandedProducts(expanded);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setExpandedProducts(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Product CRUD
  const handleSaveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", productForm.name);
      formData.append("description", productForm.description);
      formData.append("icon", productForm.icon);
      formData.append("color", productForm.color);
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success("Product created");
      }
      
      fetchProducts();
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({ name: "", description: "", icon: "package", color: "#007AFF" });
    } catch (error) {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!await confirm("Delete this product and all its sections/subsections?", { title: "Delete product", confirmLabel: "Delete" })) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || "",
      icon: product.icon || "package",
      color: product.color || "#007AFF"
    });
    setShowProductForm(true);
  };

  // Section CRUD
  const handleSaveSection = async (productId) => {
    if (!sectionForm.name.trim()) {
      toast.error("Section name is required");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", sectionForm.name);
      formData.append("description", sectionForm.description);
      
      if (editingSection) {
        await axios.put(`${API}/products/${productId}/sections/${editingSection.id}`, formData);
        toast.success("Section updated");
      } else {
        await axios.post(`${API}/products/${productId}/sections`, formData);
        toast.success("Section created");
      }
      
      fetchProducts();
      setShowSectionForm(null);
      setEditingSection(null);
      setSectionForm({ name: "", description: "" });
    } catch (error) {
      toast.error("Failed to save section");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (productId, sectionId) => {
    if (!await confirm("Delete this section and all its subsections?", { title: "Delete section", confirmLabel: "Delete" })) return;
    
    try {
      await axios.delete(`${API}/products/${productId}/sections/${sectionId}`);
      toast.success("Section deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete section");
    }
  };

  const openEditSection = (productId, section) => {
    setEditingSection(section);
    setSectionForm({
      name: section.name,
      description: section.description || ""
    });
    setShowSectionForm(productId);
  };

  // Subsection CRUD
  const handleSaveSubsection = async (productId, sectionId) => {
    if (!subsectionForm.name.trim()) {
      toast.error("Subsection name is required");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", subsectionForm.name);
      formData.append("description", subsectionForm.description);
      
      if (editingSubsection) {
        await axios.put(
          `${API}/products/${productId}/sections/${sectionId}/subsections/${editingSubsection.id}`,
          formData
        );
        toast.success("Subsection updated");
      } else {
        await axios.post(`${API}/products/${productId}/sections/${sectionId}/subsections`, formData);
        toast.success("Subsection created");
      }
      
      fetchProducts();
      setShowSubsectionForm(null);
      setEditingSubsection(null);
      setSubsectionForm({ name: "", description: "" });
    } catch (error) {
      toast.error("Failed to save subsection");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubsection = async (productId, sectionId, subsectionId) => {
    if (!await confirm("Delete this subsection?", { title: "Delete subsection", confirmLabel: "Delete" })) return;
    
    try {
      await axios.delete(`${API}/products/${productId}/sections/${sectionId}/subsections/${subsectionId}`);
      toast.success("Subsection deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete subsection");
    }
  };

  const openEditSubsection = (productId, sectionId, subsection) => {
    setEditingSubsection(subsection);
    setSubsectionForm({
      name: subsection.name,
      description: subsection.description || ""
    });
    setShowSubsectionForm({ productId, sectionId });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-hierarchy-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Product Hierarchy</h3>
          <p className="text-sm text-[#64748B]">
            Organize your products into sections and subsections for focused RFP analysis
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingProduct(null);
            setProductForm({ name: "", description: "", icon: "gauge", color: "#007AFF" });
            setShowProductForm(true);
          }}
          className="bg-[#007AFF] hover:bg-[#0066DD]"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Product Form Dialog */}
      <Dialog open={showProductForm} onOpenChange={setShowProductForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="productName">Product Name *</Label>
              <Input
                id="productName"
                placeholder="e.g., Water Analyzers, Flow Meters"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="productDesc">Description</Label>
              <Textarea
                id="productDesc"
                placeholder="Brief description of this product category (e.g., Analytical instruments for water quality monitoring)"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={productForm.icon} onValueChange={(v) => setProductForm({ ...productForm, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select value={productForm.color} onValueChange={(v) => setProductForm({ ...productForm, color: v })}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: productForm.color }}></div>
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: opt.value }}></div>
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowProductForm(false)}>Cancel</Button>
              <Button onClick={handleSaveProduct} disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingProduct ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Products List */}
      {products.length === 0 ? (
        <Card className="border-[#E2E8F0] border-dashed">
          <CardContent className="py-12 text-center">
            <Gauge className="w-12 h-12 mx-auto text-[#E2E8F0] mb-4" />
            <p className="text-lg font-semibold text-[#0F172A] mb-2">No Products Yet</p>
            <p className="text-[#64748B] mb-4">
              Create your first product to start organizing your RFP analysis (e.g., Water Analyzers, Flow Meters, IIoT Systems)
            </p>
            <Button
              onClick={() => setShowProductForm(true)}
              className="bg-[#007AFF] hover:bg-[#0066DD]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {products.map((product) => (
            <Card key={product.id} className="border-[#E2E8F0]" data-testid={`product-${product.id}`}>
              {/* Product Header */}
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                onClick={() => toggleProduct(product.id)}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${product.color}20` }}
                >
                  {React.createElement(getIconComponent(product.icon), {
                    className: "w-5 h-5",
                    style: { color: product.color }
                  })}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-[#0F172A]">{product.name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {product.sections?.length || 0} sections
                    </Badge>
                  </div>
                  {product.description && (
                    <p className="text-sm text-[#64748B]">{product.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openEditProduct(product); }}
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedProducts[product.id] ? (
                    <ChevronDown className="w-5 h-5 text-[#64748B]" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-[#64748B]" />
                  )}
                </div>
              </div>

              {/* Sections */}
              {expandedProducts[product.id] && (
                <div className="border-t border-[#E2E8F0] bg-[#F8FAFC]">
                  <div className="p-4 space-y-3">
                    {/* Add Section Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingSection(null);
                        setSectionForm({ name: "", description: "" });
                        setShowSectionForm(product.id);
                      }}
                      className="w-full border-dashed"
                      data-testid={`add-section-${product.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Section
                    </Button>

                    {/* Section Form */}
                    {showSectionForm === product.id && (
                      <Card className="border-[#007AFF]/30 bg-white">
                        <CardContent className="p-4 space-y-3">
                          <Input
                            placeholder="Section name (e.g., ERP)"
                            value={sectionForm.name}
                            onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                            data-testid="section-name-input"
                          />
                          <Textarea
                            placeholder="Description (optional)"
                            value={sectionForm.description}
                            onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                            rows={2}
                          />
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowSectionForm(null)}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveSection(product.id)}
                              disabled={saving}
                              className="bg-[#007AFF] hover:bg-[#0066DD]"
                              data-testid="save-section-btn"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSection ? "Update" : "Add")}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Sections List */}
                    {product.sections?.map((section) => (
                      <div key={section.id} className="bg-white rounded-lg border border-[#E2E8F0]" data-testid={`section-${section.id}`}>
                        {/* Section Header */}
                        <div
                          className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                          onClick={() => toggleSection(section.id)}
                        >
                          <Layers className="w-4 h-4 text-[#64748B]" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#0F172A]">{section.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {section.subsections?.length || 0} subsections
                              </Badge>
                            </div>
                            {section.description && (
                              <p className="text-xs text-[#64748B]">{section.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); openEditSection(product.id, section); }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={(e) => { e.stopPropagation(); handleDeleteSection(product.id, section.id); }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {expandedSections[section.id] ? (
                              <ChevronDown className="w-4 h-4 text-[#64748B]" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-[#64748B]" />
                            )}
                          </div>
                        </div>

                        {/* Subsections */}
                        {expandedSections[section.id] && (
                          <div className="border-t border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2">
                            {/* Add Subsection Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingSubsection(null);
                                setSubsectionForm({ name: "", description: "" });
                                setShowSubsectionForm({ productId: product.id, sectionId: section.id });
                              }}
                              className="w-full border-dashed text-xs"
                              data-testid={`add-subsection-${section.id}`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Subsection
                            </Button>

                            {/* Subsection Form */}
                            {showSubsectionForm?.productId === product.id && showSubsectionForm?.sectionId === section.id && (
                              <Card className="border-[#007AFF]/30 bg-white">
                                <CardContent className="p-3 space-y-2">
                                  <Input
                                    placeholder="Subsection name (e.g., Finance Module)"
                                    value={subsectionForm.name}
                                    onChange={(e) => setSubsectionForm({ ...subsectionForm, name: e.target.value })}
                                    className="text-sm"
                                    data-testid="subsection-name-input"
                                  />
                                  <Textarea
                                    placeholder="Description (optional)"
                                    value={subsectionForm.description}
                                    onChange={(e) => setSubsectionForm({ ...subsectionForm, description: e.target.value })}
                                    rows={2}
                                    className="text-sm"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setShowSubsectionForm(null)}>
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveSubsection(product.id, section.id)}
                                      disabled={saving}
                                      className="bg-[#007AFF] hover:bg-[#0066DD]"
                                      data-testid="save-subsection-btn"
                                    >
                                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSubsection ? "Update" : "Add")}
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Subsections List */}
                            {section.subsections?.length > 0 ? (
                              <div className="space-y-1">
                                {section.subsections.map((subsection) => (
                                  <div
                                    key={subsection.id}
                                    className="flex items-center gap-2 p-2 bg-white rounded border border-[#E2E8F0] group"
                                    data-testid={`subsection-${subsection.id}`}
                                  >
                                    <FileText className="w-3 h-3 text-[#94A3B8]" />
                                    <div className="flex-1">
                                      <span className="text-sm text-[#0F172A]">{subsection.name}</span>
                                      {subsection.description && (
                                        <p className="text-xs text-[#64748B]">{subsection.description}</p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => openEditSubsection(product.id, section.id, subsection)}
                                      >
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                        onClick={() => handleDeleteSubsection(product.id, section.id, subsection.id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-[#94A3B8] text-center py-2">
                                No subsections yet
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {(!product.sections || product.sections.length === 0) && (
                      <p className="text-sm text-[#94A3B8] text-center py-4">
                        No sections yet. Add sections to organize this product.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Summary */}
      {products.length > 0 && (
        <Card className="border-[#E2E8F0] bg-[#F8FAFC]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#64748B]">Total Structure:</span>
              <div className="flex items-center gap-4">
                <Badge variant="outline">
                  <Package className="w-3 h-3 mr-1" />
                  {products.length} Products
                </Badge>
                <Badge variant="outline">
                  <Layers className="w-3 h-3 mr-1" />
                  {products.reduce((acc, p) => acc + (p.sections?.length || 0), 0)} Sections
                </Badge>
                <Badge variant="outline">
                  <FileText className="w-3 h-3 mr-1" />
                  {products.reduce((acc, p) => acc + (p.sections?.reduce((a, s) => a + (s.subsections?.length || 0), 0) || 0), 0)} Subsections
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductHierarchyManager;
